import { useState } from 'react';
import { USE_CASES, MQM_DEFAULT_PATHS, SQL_GROUPS, SQL_CFGS, SQL_README } from '../constants/useCases';
import { FileUpload } from './FileUpload';
import type {
  UseCaseConfig,
  UseCaseKey,
  Environment,
  GenericConfig,
  OracleConfig,
  MqmConfig,
  EsxiPingConfig,       // NEW
  HttpcheckConfig,
  ProcMonConfig,
  SqlConfig,
  SqlBlock,
  TemplateConfig,
} from '../types';

interface Props {
  jobOrder: UseCaseKey[];
  jobs: Record<string, UseCaseConfig>;
  onUpdate: (key: UseCaseKey, cfg: UseCaseConfig) => void;
  onRemove: (key: UseCaseKey) => void;
}

export function ConfigBuilder({ jobOrder, jobs, onUpdate, onRemove }: Props) {
  if (jobOrder.length === 0) {
    return (
      <div className="card" style={{ padding: 30, textAlign: 'center', color: 'var(--text-dim)' }}>
        No use cases selected yet. Pick at least one in Step 2.
      </div>
    );
  }
  return (
    <div>
      {jobOrder.map(k => (
        <JobCard
          key={k}
          ucKey={k}
          config={jobs[k]}
          onUpdate={c => onUpdate(k, c)}
          onRemove={() => onRemove(k)}
        />
      ))}
    </div>
  );
}

function JobCard({
  ucKey,
  config,
  onUpdate,
  onRemove,
}: {
  ucKey: UseCaseKey;
  config: UseCaseConfig;
  onUpdate: (c: UseCaseConfig) => void;
  onRemove: () => void;
}) {
  const [open, setOpen] = useState(true);
  const def = USE_CASES[ucKey];

  const countLabel = (() => {
    if (config.type === 'sql') return `${config.blocks.length} block${config.blocks.length === 1 ? '' : 's'}`;
    if (config.type === 'httpcheck') return `${config.targets.length} target${config.targets.length === 1 ? '' : 's'}`;
    if (config.type === 'template') return config.uploadId ? 'template uploaded' : 'no template';
    if (config.type === 'esxiping') return `${config.hosts.length} host${config.hosts.length === 1 ? '' : 's'} · ${config.action}`;
    if ('hosts' in config) return `${config.hosts.length} host${config.hosts.length === 1 ? '' : 's'}`;
    return '';
  })();

  return (
    <div className="job-card">
      <div className="jh">
        <div className="jhc" onClick={() => setOpen(o => !o)}>
          <div
            className="jbadge"
            style={{
              background: `rgba(${def.rgb}, 0.15)`,
              color: def.color,
              border: `1px solid rgba(${def.rgb}, 0.25)`,
            }}
          >
            <span aria-hidden>{def.icon}</span> {def.label}
          </div>
          <span className="jtitle">{def.label} Configuration</span>
          <span className="jhcount">{countLabel}</span>
          <span className={`jtog${open ? ' open' : ''}`}>▾</span>
        </div>
        <button type="button" className="rm-btn" onClick={onRemove} aria-label={`Remove ${def.label}`}>
          ✕ Remove
        </button>
      </div>
      {open && <div className="jbody open">{renderBody(ucKey, config, onUpdate)}</div>}
    </div>
  );
}

// ─── per-type renderers ──────────────────────────────────────────────────

function renderBody(
  ucKey: UseCaseKey,
  config: UseCaseConfig,
  onUpdate: (c: UseCaseConfig) => void,
) {
  switch (config.type) {
    case 'generic':
      return <GenericBody ucKey={ucKey} config={config} onUpdate={onUpdate} />;
    case 'oracle':
      return <OracleBody config={config} onUpdate={onUpdate} />;
    case 'mqm':
      return <MqmBody config={config} onUpdate={onUpdate} />;
    case 'esxiping':
      return <EsxiPingBody config={config} onUpdate={onUpdate} />;
    case 'template':
      return <TemplateBody config={config} onUpdate={onUpdate} />;
    case 'httpcheck':
      return <HttpcheckBody config={config} onUpdate={onUpdate} />;
    case 'procmon':
      return <ProcMonBody config={config} onUpdate={onUpdate} />;
    case 'sql':
      return <SqlBody config={config} onUpdate={onUpdate} />;
  }
}

function GenericBody({
  ucKey,
  config,
  onUpdate,
}: {
  ucKey: UseCaseKey;
  config: GenericConfig;
  onUpdate: (c: GenericConfig) => void;
}) {
  const def = USE_CASES[ucKey];
  const params = def.params ?? [];

  const addRow = () => {
    const p: Record<string, string> = {};
    params.forEach(x => (p[x.id] = x.defaultValue ?? ''));
    const newHost = { id: `u_${Math.random().toString(36).slice(2)}`, hostname: '', env: 'prod' as Environment, appName: '', params: p };
    onUpdate({ ...config, hosts: [...config.hosts, newHost] });
  };

  const updateHost = (i: number, patch: Partial<GenericConfig['hosts'][number]>) => {
    const next = [...config.hosts];
    next[i] = { ...next[i], ...patch };
    onUpdate({ ...config, hosts: next });
  };

  const updateParam = (i: number, paramId: string, value: string) => {
    const next = [...config.hosts];
    next[i] = { ...next[i], params: { ...next[i].params, [paramId]: value } };
    onUpdate({ ...config, hosts: next });
  };

  const removeRow = (i: number) =>
    onUpdate({ ...config, hosts: config.hosts.filter((_, idx) => idx !== i) });

  return (
    <>
      <div className="htw">
        <table className="ht">
          <thead>
            <tr>
              <th>Hostname / URL</th>
              <th>Env</th>
              <th>app_name</th>
              {params.map(p => (
                <th key={p.id}>{p.label}</th>
              ))}
              <th />
            </tr>
          </thead>
          <tbody>
            {config.hosts.map((h, i) => (
              <tr key={h.id}>
                <td>
                  <input
                    className="hi"
                    type="text"
                    value={h.hostname}
                    onChange={e => updateHost(i, { hostname: e.target.value })}
                    placeholder="hostname.lilly.com"
                    aria-label="Hostname"
                  />
                </td>
                <td>
                  <select
                    className="si"
                    value={h.env}
                    onChange={e => updateHost(i, { env: e.target.value as Environment })}
                    aria-label="Environment"
                  >
                    <option value="prod">PROD</option>
                    <option value="qa">QA</option>
                    <option value="dev">DEV</option>
                  </select>
                </td>
                <td>
                  <input
                    className="hi"
                    type="text"
                    value={h.appName}
                    onChange={e => updateHost(i, { appName: e.target.value })}
                    placeholder="app_name"
                    aria-label="app_name"
                  />
                </td>
                {params.map(p => (
                  <td key={p.id}>
                    {p.isSelect ? (
                      <select
                        className="si"
                        value={h.params[p.id] ?? p.defaultValue ?? ''}
                        onChange={e => updateParam(i, p.id, e.target.value)}
                        aria-label={p.label}
                      >
                        {(p.options ?? []).map(o => (
                          <option key={o} value={o}>
                            {o}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <input
                        className="pi"
                        type="text"
                        value={h.params[p.id] ?? ''}
                        onChange={e => updateParam(i, p.id, e.target.value)}
                        placeholder={p.placeholder}
                        aria-label={p.label}
                      />
                    )}
                  </td>
                ))}
                <td>
                  <button
                    type="button"
                    className="del-btn"
                    onClick={() => removeRow(i)}
                    aria-label="Remove row"
                  >
                    ×
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <button type="button" className="add-row-btn" onClick={addRow}>
        + Add host
      </button>
    </>
  );
}

function OracleBody({
  config,
  onUpdate,
}: {
  config: OracleConfig;
  onUpdate: (c: OracleConfig) => void;
}) {
  const updateHost = (i: number, patch: Partial<OracleConfig['hosts'][number]>) => {
    const next = [...config.hosts];
    next[i] = { ...next[i], ...patch };
    onUpdate({ ...config, hosts: next });
  };
  const removeRow = (i: number) =>
    onUpdate({ ...config, hosts: config.hosts.filter((_, idx) => idx !== i) });
  const addRow = () =>
    onUpdate({
      ...config,
      hosts: [
        ...config.hosts,
        { id: `u_${Math.random().toString(36).slice(2)}`, hostname: '', env: 'prod' as Environment },
      ],
    });

  return (
    <>
      <div className="ora-upload">
        <div className="info-note">
          <span>📎</span>
          <span>
            Upload your <code>oracle.txt</code> with connection details. It will be attached to the
            ServiceNow RITM.
          </span>
        </div>
        <FileUpload
          accept=".yaml,.yml"
          label="Upload oracle.txt"
          onUploaded={info => onUpdate({ ...config, uploadId: info.uploadId })}
        />
      </div>

      <div className="htw">
        <table className="ht">
          <thead>
            <tr>
              <th>Hostname</th>
              <th>Env</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {config.hosts.map((h, i) => (
              <tr key={h.id}>
                <td>
                  <input
                    className="hi"
                    value={h.hostname}
                    placeholder="oracle-host.lilly.com"
                    onChange={e => updateHost(i, { hostname: e.target.value })}
                  />
                </td>
                <td>
                  <select
                    className="si"
                    value={h.env}
                    onChange={e => updateHost(i, { env: e.target.value as Environment })}
                  >
                    <option value="prod">PROD</option>
                    <option value="qa">QA</option>
                    <option value="dev">DEV</option>
                  </select>
                </td>
                <td>
                  <button type="button" className="del-btn" onClick={() => removeRow(i)} aria-label="Remove">
                    ×
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <button type="button" className="add-row-btn" onClick={addRow}>
        + Add host
      </button>
    </>
  );
}


function HttpcheckBody({
  config,
  onUpdate,
}: {
  config: HttpcheckConfig;
  onUpdate: (c: HttpcheckConfig) => void;
}) {
  const uid = () => `t_${Math.random().toString(36).slice(2, 10)}`;

  const updateTarget = (i: number, patch: Partial<HttpcheckConfig['targets'][number]>) => {
    const next = [...config.targets];
    next[i] = { ...next[i], ...patch };
    onUpdate({ ...config, targets: next });
  };

  const addTarget = () =>
    onUpdate({
      ...config,
      targets: [
        ...config.targets,
        {
          id: uid(),
          endpoint: '',
          method: 'GET',
          env: 'prod' as Environment,
          appName: '',
          timeout: '10s',
          expectedStatus: '200',
          tlsSkipVerify: false,
        },
      ],
    });

  const removeTarget = (i: number) => {
    if (config.targets.length <= 1) return;
    onUpdate({ ...config, targets: config.targets.filter((_, idx) => idx !== i) });
  };

  return (
    <>
      <div className="info-note blue" style={{ marginBottom: 12 }}>
        <span>Info</span>
        <span>
          Based on OTel <strong>httpcheck receiver</strong>. Each target is an
          independent HTTP probe with its own endpoint, method, headers, and
          validation rules.{' '}
	  <a
            href="https://github.com/open-telemetry/opentelemetry-collector-contrib/tree/main/receiver/httpcheckreceiver"
            target="_blank"
            rel="noreferrer"
            style={{ color: '#93bbff' }}
          >
            Receiver Docs
          </a>
        </span>
      </div>

      <div className="fgrid2" style={{ marginBottom: 14 }}>
        <div className="field">
          <label className="flbl">
            Collection Interval <span className="req">*</span>
          </label>
          <select
            className="finput"
            value={config.collectionInterval}
            onChange={e => onUpdate({ ...config, collectionInterval: e.target.value })}
          >
            <option value="30s">30s</option>
            <option value="60s">60s (default)</option>
            <option value="120s">120s</option>
            <option value="300s">300s</option>
          </select>
        </div>
        <div className="field">
          <label className="flbl">Initial Delay</label>
          <select
            className="finput"
            value={config.initialDelay ?? '1s'}
            onChange={e => onUpdate({ ...config, initialDelay: e.target.value })}
          >
            <option value="1s">1s (default)</option>
            <option value="5s">5s</option>
            <option value="10s">10s</option>
            <option value="30s">30s</option>
          </select>
        </div>
      </div>

      <div style={{ marginBottom: 8 }}>
        <div className="flbl">
          Targets <span className="req">*</span>
          <span
            style={{
              fontSize: 10,
              fontWeight: 400,
              color: 'var(--text-faint)',
              marginLeft: 8,
              fontFamily: "'DM Mono', monospace",
              textTransform: 'none',
            }}
          >
            Each target = one HTTP probe with its own parameters
          </span>
        </div>
      </div>

      {config.targets.map((t, i) => (
        <div
          key={t.id}
          className="hc-tcard"
          style={{
            border: '1px solid var(--border-soft, #1c222c)',
            borderRadius: 8,
            padding: 14,
            marginBottom: 12,
            background: 'rgba(139, 92, 246, 0.04)',
          }}
        >
          <div
            className="hc-th"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              marginBottom: 12,
            }}
          >
            <div
              style={{
                width: 24,
                height: 24,
                borderRadius: '50%',
                background: 'rgba(139, 92, 246, 0.2)',
                color: '#a78bfa',
                fontWeight: 600,
                display: 'grid',
                placeItems: 'center',
                fontSize: 12,
              }}
            >
              {i + 1}
            </div>
            <div
              style={{
                flex: 1,
                fontFamily: "'DM Mono', monospace",
                fontSize: 12,
                color: 'var(--text)',
              }}
            >
              {t.endpoint || 'Enter endpoint below'}
            </div>
            <select
              className="si"
              style={{ width: 78 }}
              value={t.env}
              onChange={e => updateTarget(i, { env: e.target.value as Environment })}
            >
              <option value="prod">PROD</option>
              <option value="qa">QA</option>
              <option value="dev">DEV</option>
            </select>
            <button
              type="button"
              className="del-btn"
              onClick={() => removeTarget(i)}
              aria-label="Remove target"
            >
              ×
            </button>
          </div>

          <div className="fgrid3" style={{ marginBottom: 10 }}>
            <div className="field">
              <label className="flbl">
                Endpoint <span className="req">*</span>
              </label>
              <input
                className="finput"
                type="text"
                value={t.endpoint}
                placeholder="https://app.lilly.com/health"
                onChange={e => updateTarget(i, { endpoint: e.target.value })}
              />
            </div>
            <div className="field">
              <label className="flbl">HTTP Method</label>
              <select
                className="finput"
                value={t.method}
                onChange={e =>
                  updateTarget(i, { method: e.target.value as 'GET' | 'POST' | 'PUT' | 'HEAD' })
                }
              >
                <option value="GET">GET</option>
                <option value="POST">POST</option>
                <option value="HEAD">HEAD</option>
                <option value="PUT">PUT</option>
              </select>
            </div>
            <div className="field">
              <label className="flbl">Timeout</label>
              <select
                className="finput"
                value={t.timeout}
                onChange={e => updateTarget(i, { timeout: e.target.value })}
              >
                <option value="5s">5s</option>
                <option value="10s">10s (default)</option>
                <option value="30s">30s</option>
              </select>
            </div>
          </div>

          <div className="fgrid3" style={{ marginBottom: 10 }}>
            <div className="field">
              <label className="flbl">Expected Status Code</label>
              <input
                className="finput"
                value={t.expectedStatus}
                placeholder="200"
                onChange={e => updateTarget(i, { expectedStatus: e.target.value })}
              />
              <span className="fhint">Leave blank to skip validation</span>
            </div>
            <div className="field">
              <label className="flbl">app_name Tag</label>
              <input
                className="finput"
                value={t.appName}
                placeholder="cam_sesto"
                onChange={e => updateTarget(i, { appName: e.target.value })}
              />
              <span className="fhint" style={{ color: 'var(--amber, #f4b95a)' }}>
                Optional if already set
              </span>
            </div>
            <div className="field">
              <label className="flbl">TLS Skip Verify?</label>
              <select
                className="finput"
                value={t.tlsSkipVerify ? 'Yes' : 'No'}
                onChange={e => updateTarget(i, { tlsSkipVerify: e.target.value === 'Yes' })}
              >
                <option value="No">No (verify TLS)</option>
                <option value="Yes">Yes (skip verify)</option>
              </select>
            </div>
          </div>

          <div className="fgrid2">
            <div className="field">
              <label className="flbl">Custom Headers</label>
              <input
                className="finput"
                value={t.customHeaders ?? ''}
                placeholder='Authorization: "Bearer TOKEN", Accept: "application/json"'
                onChange={e => updateTarget(i, { customHeaders: e.target.value })}
              />
              <span className="fhint">Comma-separated key: value pairs</span>
            </div>
            <div className="field">
              <label className="flbl">Request Body (POST/PUT)</label>
              <input
                className="finput"
                value={t.body ?? ''}
                placeholder='{"check": "full"}'
                onChange={e => updateTarget(i, { body: e.target.value })}
              />
              <span className="fhint">JSON body string if method is POST/PUT</span>
            </div>
          </div>
        </div>
      ))}

      <button type="button" className="add-row-btn" onClick={addTarget}>
        + Add Target
      </button>
    </>
  );
}


function ProcMonBody({
  config,
  onUpdate,
}: {
  config: ProcMonConfig;
  onUpdate: (c: ProcMonConfig) => void;
}) {
  const uid = () => `p_${Math.random().toString(36).slice(2, 10)}`;

  const updateHost = (i: number, patch: Partial<ProcMonConfig['hosts'][number]>) => {
    const next = [...config.hosts];
    next[i] = { ...next[i], ...patch };
    onUpdate({ ...config, hosts: next });
  };

  const addHost = () =>
    onUpdate({
      ...config,
      hosts: [
        ...config.hosts,
        {
          id: uid(),
          hostname: '',
          env: 'prod' as Environment,
          appName: '',
          osType: 'Linux',
          include: [],
          matchType: 'strict',
          scrapeDelay: '0s',
          muteErrors: {},
          metrics: {},
        },
      ],
    });

  const removeHost = (i: number) => {
    if (config.hosts.length <= 1) return;
    onUpdate({ ...config, hosts: config.hosts.filter((_, idx) => idx !== i) });
  };

  return (
    <>
      <div className="info-note blue" style={{ marginBottom: 12 }}>
        <span>Info</span>
        <span>
          Based on OTel hostmetrics receiver process scraper. Collects CPU,
          memory, disk I/O, and thread metrics per process.
        </span>
      </div>

      <div style={{ marginBottom: 8 }}>
        <div className="flbl">
          Target Hosts <span className="req">*</span>
          <span
            style={{
              fontSize: 10,
              fontWeight: 400,
              color: 'var(--text-faint)',
              marginLeft: 8,
              fontFamily: "'DM Mono', monospace",
              textTransform: 'none',
            }}
          >
            Configure per host - process include/exclude filter, match type, options
          </span>
        </div>
      </div>

      {config.hosts.map((h, i) => (
        <div
          key={h.id}
          className="proc-block"
          style={{
            border: '1px solid var(--border-soft, #1c222c)',
            borderRadius: 8,
            padding: 14,
            marginBottom: 12,
            background: 'rgba(236, 72, 153, 0.04)',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              marginBottom: 12,
            }}
          >
            <div
              style={{
                width: 24,
                height: 24,
                borderRadius: '50%',
                background: 'rgba(236, 72, 153, 0.2)',
                color: '#ec4899',
                fontWeight: 600,
                display: 'grid',
                placeItems: 'center',
                fontSize: 12,
              }}
            >
              {i + 1}
            </div>
            <div
              style={{
                flex: 1,
                fontFamily: "'DM Mono', monospace",
                fontSize: 12,
                color: 'var(--text)',
              }}
            >
              {h.hostname || 'Enter hostname below'}
            </div>
            <div
              style={{
                fontSize: 10,
                fontFamily: "'DM Mono', monospace",
                padding: '3px 8px',
                borderRadius: 3,
                background: 'rgba(236, 72, 153, 0.15)',
                color: '#ec4899',
                border: '1px solid rgba(236, 72, 153, 0.3)',
                letterSpacing: '0.04em',
                textTransform: 'uppercase',
              }}
            >
              process scraper
            </div>
            <select
              className="si"
              style={{ width: 72 }}
              value={h.env}
              onChange={e => updateHost(i, { env: e.target.value as Environment })}
            >
              <option value="prod">PROD</option>
              <option value="qa">QA</option>
              <option value="dev">DEV</option>
            </select>
            <button
              type="button"
              className="del-btn"
              onClick={() => removeHost(i)}
              aria-label="Remove host"
            >
              x
            </button>
          </div>

          <div className="fgrid3" style={{ marginBottom: 10 }}>
            <div className="field">
              <label className="flbl">
                Hostname (FQDN) <span className="req">*</span>
              </label>
              <input
                className="finput"
                value={h.hostname}
                placeholder="myserver.am.lilly.com"
                onChange={e => updateHost(i, { hostname: e.target.value })}
              />
            </div>
            <div className="field">
              <label className="flbl">OS Type</label>
              <select
                className="finput"
                value={h.osType}
                onChange={e => updateHost(i, { osType: e.target.value as 'Linux' | 'Windows' })}
              >
                <option value="Linux">Linux</option>
                <option value="Windows">Windows</option>
              </select>
            </div>
            <div className="field">
              <label className="flbl">app_name Tag</label>
              <input
                className="finput"
                value={h.appName}
                placeholder="cam_sesto"
                onChange={e => updateHost(i, { appName: e.target.value })}
              />
              <span className="fhint" style={{ color: 'var(--amber, #f4b95a)' }}>
                Optional if already set
              </span>
            </div>
          </div>

          <div className="fgrid2" style={{ marginBottom: 10 }}>
            <div className="field">
              <label className="flbl">
                Process Names to Include <span className="req">*</span>
              </label>
              <input
                className="finput"
                key={h.id}
                defaultValue={(h.include || []).join(', ')}
                placeholder="java, nginx, python3"
                onBlur={e =>
                  updateHost(i, {
                    include: e.target.value
                      .split(',')
                      .map(s => s.trim())
                      .filter(Boolean),
                  })
                }
              />
              <span className="fhint">
                Comma-separated. Supports regexp if match type = regexp
              </span>
            </div>
            <div className="field">
              <label className="flbl">Match Type</label>
              <select
                className="finput"
                value={h.matchType}
                onChange={e =>
                  updateHost(i, { matchType: e.target.value as 'strict' | 'regexp' })
                }
              >
                <option value="strict">strict - exact name match</option>
                <option value="regexp">regexp - regex pattern match</option>
              </select>
              <span className="fhint">strict = exact string; regexp = e.g. ^java.*</span>
            </div>
          </div>

          <div className="field" style={{ maxWidth: 360 }}>
            <label className="flbl">Scrape Process Delay</label>
            <select
              className="finput"
              value={h.scrapeDelay}
              onChange={e => updateHost(i, { scrapeDelay: e.target.value })}
            >
              <option value="0s">0s - no delay (default)</option>
              <option value="5s">5s</option>
              <option value="10s">10s</option>
              <option value="30s">30s</option>
            </select>
            <span className="fhint">
              Delay before starting to scrape short-lived processes
            </span>
          </div>
        </div>
      ))}

      <button type="button" className="add-row-btn" onClick={addHost}>
        + Add Host
      </button>
    </>
  );
}


function SqlBody({
  config,
  onUpdate,
}: {
  config: SqlConfig;
  onUpdate: (c: SqlConfig) => void;
}) {
  const setGroup = (group: string) => {
    // Selecting a new group resets the config-file selection and the host blocks
    onUpdate({ ...config, group, cfgKey: '', blocks: [] });
  };

  return (
    <>
      <div style={{ marginBottom: 10 }}>
        <span
          style={{
            fontSize: 11,
            color: '#4ade80',
            fontFamily: "'DM Mono', monospace",
            letterSpacing: '0.04em',
          }}
        >
          Reference: splunk_hcs_SQL README on GitHub
        </span>
        <br />
        <span style={{ fontSize: 10, color: 'var(--text-faint)', fontFamily: "'DM Mono', monospace" }}>
          {SQL_README}
        </span>
      </div>

      <div style={{ marginBottom: 14 }}>
        <div className="flbl" style={{ marginBottom: 8 }}>
          <span
            style={{
              display: 'inline-grid',
              placeItems: 'center',
              width: 18,
              height: 18,
              background: 'rgba(22, 163, 74, 0.2)',
              color: '#16a34a',
              borderRadius: '50%',
              fontSize: 11,
              fontWeight: 600,
              marginRight: 6,
              verticalAlign: 'middle',
            }}
          >
            A
          </span>
          Select Agent Config Group
          <span
            style={{
              fontSize: 10,
              fontWeight: 400,
              color: 'var(--text-faint)',
              marginLeft: 8,
              fontFamily: "'DM Mono', monospace",
              textTransform: 'none',
            }}
          >
            AG = Always On Availability Group
          </span>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {SQL_GROUPS.map(g => (
            <button
              key={g}
              type="button"
              onClick={() => setGroup(g)}
              style={{
                padding: '8px 14px',
                fontSize: 12,
                fontFamily: "'DM Mono', monospace",
                fontWeight: 600,
                letterSpacing: '0.04em',
                background: config.group === g ? 'rgba(22, 163, 74, 0.18)' : 'transparent',
                color: config.group === g ? '#16a34a' : 'var(--text-dim)',
                border: config.group === g ? '1px solid rgba(22, 163, 74, 0.5)' : '1px solid var(--border)',
                borderRadius: 4,
                cursor: 'pointer',
              }}
            >
              {g}
            </button>
          ))}
        </div>
      </div>

      {config.group && (
        <div style={{ marginBottom: 14 }}>
          <div className="flbl" style={{ marginBottom: 8 }}>
            <span
              style={{
                display: 'inline-grid',
                placeItems: 'center',
                width: 18,
                height: 18,
                background: 'rgba(22, 163, 74, 0.2)',
                color: '#16a34a',
                borderRadius: '50%',
                fontSize: 11,
                fontWeight: 600,
                marginRight: 6,
                verticalAlign: 'middle',
              }}
            >
              B
            </span>
            Select Agent Config File
            <span
              style={{
                fontSize: 10,
                fontWeight: 400,
                color: 'var(--text-faint)',
                marginLeft: 8,
                fontFamily: "'DM Mono', monospace",
                textTransform: 'none',
              }}
            >
              Based on number of SQL instances per host
            </span>
          </div>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
              gap: 10,
            }}
          >
            {Object.entries(SQL_CFGS).map(([ck, cfg]) => {
              const active = config.cfgKey === ck;
              return (
                <button
                  key={ck}
                  type="button"
                  onClick={() => onUpdate({ ...config, cfgKey: ck, blocks: [] })}
                  style={{
                    textAlign: 'left',
                    padding: '12px 14px',
                    background: active ? 'rgba(22, 163, 74, 0.12)' : 'var(--bg-card, #161b24)',
                    border: active
                      ? '1px solid rgba(22, 163, 74, 0.5)'
                      : '1px solid var(--border, #232a36)',
                    borderRadius: 6,
                    cursor: 'pointer',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 4,
                  }}
                >
                  <div
                    style={{
                      fontSize: 13,
                      fontWeight: 600,
                      color: active ? '#16a34a' : 'var(--text)',
                    }}
                  >
                    {cfg.label}
                  </div>
                  <div
                    style={{
                      fontSize: 10,
                      fontFamily: "'DM Mono', monospace",
                      color: 'var(--text-faint)',
                    }}
                  >
                    {cfg.file}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {config.group && config.cfgKey && (() => {
        const cfg = SQL_CFGS[config.cfgKey];
        const uid = () => `sb_${Math.random().toString(36).slice(2, 10)}`;

        const ensureFirstBlock = () => {
          if (config.blocks.length === 0) {
            const initialVars: Record<string, string> = {};
            cfg.vars.forEach(v => (initialVars[v.k] = ''));
            const first: SqlBlock = { id: uid(), env: 'prod' as Environment, vars: initialVars };
            onUpdate({ ...config, blocks: [first] });
          }
        };
        ensureFirstBlock();

        const updateBlock = (i: number, patch: Partial<SqlBlock>) => {
          const next = [...config.blocks];
          next[i] = { ...next[i], ...patch };
          onUpdate({ ...config, blocks: next });
        };

        const updateVar = (i: number, varKey: string, value: string) => {
          const next = [...config.blocks];
          next[i] = { ...next[i], vars: { ...next[i].vars, [varKey]: value } };
          onUpdate({ ...config, blocks: next });
        };

        const addBlock = () => {
          const initialVars: Record<string, string> = {};
          cfg.vars.forEach(v => (initialVars[v.k] = ''));
          onUpdate({
            ...config,
            blocks: [
              ...config.blocks,
              { id: uid(), env: 'prod' as Environment, vars: initialVars },
            ],
          });
        };

        const removeBlock = (i: number) => {
          if (config.blocks.length <= 1) return;
          onUpdate({ ...config, blocks: config.blocks.filter((_, idx) => idx !== i) });
        };

        return (
          <div style={{ marginBottom: 14 }}>
            <div className="flbl" style={{ marginBottom: 6 }}>
              <span
                style={{
                  display: 'inline-grid',
                  placeItems: 'center',
                  width: 18,
                  height: 18,
                  background: 'rgba(22, 163, 74, 0.2)',
                  color: '#16a34a',
                  borderRadius: '50%',
                  fontSize: 11,
                  fontWeight: 600,
                  marginRight: 6,
                  verticalAlign: 'middle',
                }}
              >
                C
              </span>
              Configure Host Variables
              <span
                style={{
                  fontSize: 10,
                  fontWeight: 400,
                  color: 'var(--text-faint)',
                  marginLeft: 8,
                  fontFamily: "'DM Mono', monospace",
                  textTransform: 'none',
                }}
              >
                One block per host - {cfg.file}
              </span>
            </div>

            <div
              className="info-note"
              style={{
                background: 'rgba(22, 163, 74, 0.06)',
                borderLeft: '3px solid #16a34a',
                marginBottom: 10,
              }}
            >
              <span>Info</span>
              <span>
                Variables injected into <strong>{cfg.file}</strong> for group{' '}
                <strong>{config.group}</strong>.
              </span>
            </div>

            {config.blocks.map((block, i) => (
              <div
                key={block.id}
                style={{
                  border: '1px solid var(--border-soft, #1c222c)',
                  borderRadius: 8,
                  padding: 14,
                  marginBottom: 12,
                  background: 'rgba(22, 163, 74, 0.04)',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    marginBottom: 12,
                  }}
                >
                  <div
                    style={{
                      width: 24,
                      height: 24,
                      borderRadius: '50%',
                      background: 'rgba(22, 163, 74, 0.2)',
                      color: '#16a34a',
                      fontWeight: 600,
                      display: 'grid',
                      placeItems: 'center',
                      fontSize: 12,
                    }}
                  >
                    {i + 1}
                  </div>
                  <div
                    style={{
                      flex: 1,
                      fontFamily: "'DM Mono', monospace",
                      fontSize: 12,
                      color: 'var(--text)',
                    }}
                  >
                    {block.vars['SQL_HOSTNAME']?.trim() || 'Enter hostname below'}
                  </div>
                  <div
                    style={{
                      fontSize: 10,
                      fontFamily: "'DM Mono', monospace",
                      padding: '3px 8px',
                      borderRadius: 3,
                      background: 'rgba(22, 163, 74, 0.15)',
                      color: '#16a34a',
                      border: '1px solid rgba(22, 163, 74, 0.3)',
                      letterSpacing: '0.04em',
                    }}
                  >
                    {cfg.file.replace('.yaml', '')}
                  </div>
                  <select
                    className="si"
                    style={{ width: 78 }}
                    value={block.env}
                    onChange={e =>
                      updateBlock(i, { env: e.target.value as Environment })
                    }
                  >
                    <option value="prod">PROD</option>
                    <option value="qa">QA</option>
                    <option value="dev">DEV</option>
                  </select>
                  <button
                    type="button"
                    className="del-btn"
                    onClick={() => removeBlock(i)}
                    aria-label="Remove host"
                  >
                    x
                  </button>
                </div>

                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
                    gap: 10,
                  }}
                >
                  {cfg.vars.map(v => (
                    <div key={v.k} className="field">
                      <label
                        className="flbl"
                        style={{
                          fontSize: 11,
                          fontFamily: "'DM Mono', monospace",
                          color: '#16a34a',
                        }}
                      >
                        {v.k}
                      </label>
                      <input
                        className="finput"
                        type="text"
                        value={block.vars[v.k] ?? ''}
                        placeholder={v.ph}
                        onChange={e => updateVar(i, v.k, e.target.value)}
                      />
                      <span className="fhint">{v.d}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}

            <button type="button" className="add-row-btn" onClick={addBlock}>
              + Add SQL Server Host
            </button>
          </div>
        );
      })()}
    </>
  );
}

function MqmBody({
  config,
  onUpdate,
}: {
  config: MqmConfig;
  onUpdate: (c: MqmConfig) => void;
}) {
  const updateHost = (i: number, patch: Partial<MqmConfig['hosts'][number]>) => {
    const next = [...config.hosts];
    next[i] = { ...next[i], ...patch };
    onUpdate({ ...config, hosts: next });
  };
  const removeRow = (i: number) =>
    onUpdate({ ...config, hosts: config.hosts.filter((_, idx) => idx !== i) });
  const addRow = () =>
    onUpdate({
      ...config,
      hosts: [
        ...config.hosts,
        {
          id: `u_${Math.random().toString(36).slice(2)}`,
          hostname: '',
          env: 'prod' as Environment,
          osType: 'Linux',
          // Use the central spec-derived defaults. Recomputed below when
          // the user toggles OS or env.
          dirPath: MQM_DEFAULT_PATHS.Linux.prod,
        },
      ],
    });

  return (
    <>
      <div className="htw">
        <table className="ht">
          <thead>
            <tr>
              <th>Hostname</th>
              <th>OS</th>
              <th>Env</th>
              <th>Directory Path</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {config.hosts.map((h, i) => (
              <tr key={h.id}>
                <td>
                  <input
                    className="hi"
                    value={h.hostname}
                    placeholder="mqm-host.lilly.com"
                    onChange={e => updateHost(i, { hostname: e.target.value })}
                  />
                </td>
                <td>
                  <select
                    className="si"
                    value={h.osType}
                    onChange={e => {
                      const osType = e.target.value as 'Linux' | 'Windows';
                      // Recompute the directory path to match the new OS
                      // unless the user has typed something custom. Read
                      // host fields through a typed cast since the host
                      // union's narrowed type doesn't include osType/dirPath.
                      const mqmHost = h as MqmConfig['hosts'][number];
                      const wasDefault =
                        mqmHost.dirPath === MQM_DEFAULT_PATHS[mqmHost.osType][mqmHost.env] ||
                        mqmHost.dirPath === MQM_DEFAULT_PATHS[mqmHost.osType].prod ||
                        mqmHost.dirPath === '/var/mqm/log/';
                      updateHost(i, {
                        osType,
                        ...(wasDefault && { dirPath: MQM_DEFAULT_PATHS[osType][mqmHost.env] ?? MQM_DEFAULT_PATHS[osType].prod }),
                      });
                    }}
                  >
                    <option value="Linux">Linux</option>
                    <option value="Windows">Windows</option>
                  </select>
                </td>
                <td>
                  <select
                    className="si"
                    value={h.env}
                    onChange={e => {
                      const env = e.target.value as Environment;
                      const mqmHost = h as MqmConfig['hosts'][number];
                      const wasDefault =
                        mqmHost.dirPath === MQM_DEFAULT_PATHS[mqmHost.osType][mqmHost.env] ||
                        mqmHost.dirPath === MQM_DEFAULT_PATHS[mqmHost.osType].prod ||
                        mqmHost.dirPath === '/var/mqm/log/';
                      updateHost(i, {
                        env,
                        ...(wasDefault && { dirPath: MQM_DEFAULT_PATHS[mqmHost.osType][env] ?? MQM_DEFAULT_PATHS[mqmHost.osType].prod }),
                      });
                    }}
                  >
                    <option value="prod">PROD</option>
                    <option value="qa">QA</option>
                    <option value="dev">DEV</option>
                  </select>
                </td>
                <td>
                  <input
                    className="pi"
                    value={h.dirPath}
                    onChange={e => updateHost(i, { dirPath: e.target.value })}
                  />
                </td>
                <td>
                  <button type="button" className="del-btn" onClick={() => removeRow(i)} aria-label="Remove">
                    ×
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <button type="button" className="add-row-btn" onClick={addRow}>
        + Add host
      </button>
    </>
  );
}

function TemplateBody({
  config,
  onUpdate,
}: {
  config: TemplateConfig;
  onUpdate: (c: TemplateConfig) => void;
}) {
  return (
    <div className="tpl-sec">
      <p>
        Download the template, fill in your custom monitoring requirements, then upload it. The file
        will be attached to the ServiceNow RITM for the platform team to review.
      </p>
      <div className="tpl-acts">
        <a className="tpl-dl" href="/templates/aiops-custom-template.csv" download>
          ⬇ Download Template
        </a>
        <FileUpload
          accept=".csv,.xlsx,.yaml,.yml,.json,.txt"
          label="Upload filled template"
          onUploaded={info => onUpdate({ ...config, uploadId: info.uploadId })}
        />
      </div>
    </div>
  );
}

function EsxiPingBody({
  config,
  onUpdate,
}: {
  config: EsxiPingConfig;
  onUpdate: (c: EsxiPingConfig) => void;
}) {
  const [text, setText] = useState(config.hosts.join('\n'));

  // Parse textarea → clean list on blur. Trim, drop blanks, dedupe.
  const commit = () => {
    const hosts = Array.from(
      new Set(
        text
          .split(/\r?\n/)
          .map(h => h.trim())
          .filter(Boolean),
      ),
    );
    onUpdate({ ...config, hosts });
  };

  return (
    <>
      <div className="info-note blue" style={{ marginBottom: 12 }}>
        <span>Info</span>
        <span>
          ICMP ping monitoring for VMware ESXi hosts. Bulk Add or Remove hosts
          from monitoring. Automation currently accepts one use case per RITM.
        </span>
      </div>

      <div className="field" style={{ marginBottom: 14 }}>
        <label className="flbl">
          Action <span className="req">*</span>
        </label>
        <div style={{ display: 'flex', gap: 16, marginTop: 6 }}>
          {(['Add', 'Remove'] as const).map(a => (
            <label
              key={a}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                cursor: 'pointer',
                fontSize: 13,
                color: config.action === a ? '#F59E0B' : 'var(--text-dim)',
                fontWeight: config.action === a ? 600 : 400,
              }}
            >
              <input
                type="radio"
                name={`esxi-action-${config.action || 'x'}`}
                value={a}
                checked={config.action === a}
                onChange={() => onUpdate({ ...config, action: a })}
              />
              {a}
            </label>
          ))}
        </div>
        <span className="fhint">
          Add = start monitoring listed hosts; Remove = stop and decommission
        </span>
      </div>

      <div className="field">
        <label className="flbl">
          Hostnames <span className="req">*</span>
          <span
            style={{
              fontSize: 10,
              fontWeight: 400,
              color: 'var(--text-faint)',
              marginLeft: 8,
              fontFamily: "'DM Mono', monospace",
              textTransform: 'none',
            }}
          >
            One per line — duplicates and blank lines are removed
          </span>
        </label>
        <textarea
          className="finput"
          rows={12}
          value={text}
          onChange={e => setText(e.target.value)}
          onBlur={commit}
          placeholder={
            'esxi-host-01.lilly.com\nesxi-host-02.lilly.com\nesxi-host-03.lilly.com'
          }
          style={{
            fontFamily: "'DM Mono', monospace",
            fontSize: 12,
            resize: 'vertical',
            minHeight: 220,
            width: '100%',
          }}
        />
        <span className="fhint">
          {config.hosts.length} host{config.hosts.length === 1 ? '' : 's'} parsed
        </span>
      </div>
    </>
  );
}